import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router'

import { Autocomplete, TextField } from '@mui/material'
import { useLocalStorage, writeStorage } from '@rehooks/local-storage'
import axios from 'axios'
import styled from 'styled-components'

import loginBackground from 'common/assets/images/loginBackground.svg'

import Logo from '../../common/assets/images/logo_blox.png'
import { useFetch } from '../../common/hooks/useApi'
import { InstitutionsV2Endpoint } from '../../routes/locations/endpoints/institutions'
import { LoginInstitutionLocation } from '../../routes/locations/local/login'
import('../../common/assets/css/LoginForm.css')

const ChooseInstitutionComponent = ({ history }) => {
  const [checked, setChecked] = useState(true)
  const [inputValue, setInputValue] = useState('')
  const [institutions, setInstitutions] = useState([])
  const [error, setError] = useState(false)
  const { call: getInstitutions } = useFetch({ path: InstitutionsV2Endpoint })
  const { t } = useTranslation()
  const [userInfo] = useLocalStorage('userInfo')
  const [userInstitution] = useLocalStorage('institution')
  const [userImpersonating] = useLocalStorage('user-impersonating'),
    currentYear = new Intl.DateTimeFormat('pt-BR', { year: 'numeric' }).format(new Date())

  const source = axios.CancelToken.source()

  React.useEffect(() => {
    if (!userInfo && userImpersonating) {
      localStorage.removeItem('user-impersonating')
      localStorage.removeItem('user-impersonating-headers')
    }

    localStorage.removeItem('userInfo')
    localStorage.removeItem('userName')

    if (userInstitution) {
      history.push(
        LoginInstitutionLocation.toUrl({
          institution: userInstitution.institutionId,
        }),
      )
    }

    const fetchInstitutions = async () => {
      const { data } = await getInstitutions({ source })

      setInstitutions(data)
    }

    fetchInstitutions()
  }, [])

  const onChangeInstitutition = (sample) => {
    setError(false)
    try {
      if (sample) {
        if (checked) {
          writeStorage('institution', {
            institutionId: sample.value.id,
            data: sample.value,
          })
        }

        if (sample.value?.options?.sign_in_redirect_to) {
          window.location.href = sample.value.options.sign_in_redirect_to
        } else {
          history.push(
            LoginInstitutionLocation.toUrl({
              institution: sample.value.id,
              data: sample.value,
            }),
          )
        }
      }
    } catch (e) {
      console.log(e)
      setError(true)
    }
  }

  const options = institutions.map((sample) => ({
    label: sample.name,
    value: { ...sample },
  }))

  const handleKeyPress = useCallback(
    (event) => {
      const rgx = /enter/i
      if (rgx.test(event.key)) {
        const firstOpt = options.find(({ label }) => label.toLowerCase().includes(event.target.value))

        if (firstOpt) {
          onChangeInstitutition(firstOpt)
        }
      }
    },
    [options, inputValue, onChangeInstitutition],
  )

  return (
    <Container>
      <LoginForm error={error}>
        <ContentContainer>
          <FormHeader>
            <Img src={Logo} width='170px' margin='0 0 25px 0' />
            <StyledSpan fontSize='25px' fontWeight='lighter' color='#5d5c5c' textAlign='center'>
              {t('login:intelligenceFlexible')}
            </StyledSpan>
            <ContentWrapper>
              <Hr margin='0 30px 0 0' />
              <StyledSpan fontSize='15px' color='#9b9b9b' textAlign='center' whiteSpace='nowrap'>
                {t('login:chooseInstitution')}
              </StyledSpan>
              <Hr margin='0 0 0 30px' />
            </ContentWrapper>
          </FormHeader>
          <FormBody>
            <Autocomplete
              onKeyDown={handleKeyPress}
              onChange={(_, value) => onChangeInstitutition(value)}
              options={options}
              onInputChange={(_, value) => setInputValue(value)}
              inputValue={inputValue}
              renderInput={(params) => (
                <TextField variant='standard' placeholder={t('login:whatYourInstitution')} {...params} />
              )}
              sx={{ maxHeight: 150 }}
            />
            {error && (
              <StyledSpan fontSize='11px' color='red'>
                {t('login:institutionChoose')}
              </StyledSpan>
            )}
            <ControlCheck>
              <CheckboxContent onClick={() => setChecked(!checked)}>
                <Checkbox checked={checked} />
              </CheckboxContent>
              <StyledSpan fontSize='16px' onClick={() => setChecked(!checked)} cursor='pointer'>
                {t('login:rememberInstitution')}
              </StyledSpan>
            </ControlCheck>
          </FormBody>
        </ContentContainer>

        <FormFooter>
          <Img src={Logo} width='77px' margin='0 15px 0 0' />
          <StyledSpan fontSize='12px' textAlign='center'>
            {`${t('login:footer', { year: currentYear })} `}
            <a href='https://blox.education' target='_blank' rel='noreferrer'>
              https://blox.education
            </a>
          </StyledSpan>
        </FormFooter>
      </LoginForm>
      <ContainerBackground />
    </Container>
  )
}

const Img = styled.img`
  width: ${({ width }) => width};
  ${({ margin }) => (margin ? `margin: ${margin};` : '')};
`
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-size: cover;
  background-repeat: no-repeat;
  padding: 50px 20px 50px 20px;
  height: 100%;
  background-image: url(${loginBackground});
  @media (min-width: 768px) {
    background: #fff;
    padding: 0;
    height: 100vh;
  }
  @media screen and (orientation: landscape) and(min-device-width: 768px) {
    height: 100vh;
  }
`
const LoginForm = styled.div`
  position: relative;
  width: 100%;
  min-height: 550px;
  border-radius: 10px;
  box-shadow: 0px 3px 16px #00000029;
  background-color: #fff;
  padding: 20px;
  overflow: hidden;
  ${({ error }) => (error ? 'animation: shake 1s ease-in-out;' : '')}

  @media (min-width: 768px) {
    width: 40%;
    border-radius: 0;
    padding: 0 20px;
  }
  @media screen and (orientation: landscape) {
    min-height: 550px;
  }

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }
`

const ContentContainer = styled.div`
  height: 80%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const FormHeader = styled.div`
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
  padding: 20px 0 50px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`
const FormBody = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  justify-content: center;
`
const FormFooter = styled.div`
  position: absolute;
  box-sizing: border-box;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
  align-items: center;
  justify-content: center;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`
const ContainerBackground = styled.div`
  display: none;
  min-height: 550px;
  background-image: url(${loginBackground});
  background-position: top left;
  background-size: cover;
  background-repeat: no-repeat;
  transition: all 1s ease-in-out;
  @media (min-width: 768px) {
    display: block;
    width: 60%;
  }
`
const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 25px;
`

const StyledSpan = styled.span`
  font-size: ${({ fontSize }) => fontSize};
  color: ${({ color = '#5d5c5c' }) => color};
  font-weight: ${({ fontWeight = 'normal' }) => fontWeight};
  text-align: ${({ textAlign = 'left' }) => textAlign};
  white-space: ${({ whiteSpace }) => (whiteSpace ? whiteSpace : '')};
  cursor: ${({ cursor }) => (cursor ? cursor : 'normal')};
`
const Hr = styled.hr`
  width: 138px;
  height: 1px;
  margin: ${({ margin }) => margin};
  background-color: #9b9b9b;
`
const CheckboxContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: transparent;
  border: 4px solid #39414a60;
  margin-right: 15px;
  clip-path: circle(40%);
  &:hover {
    cursor: pointer;
  }
`
const Checkbox = styled.div(
  ({ checked }) => `
  transform: scale(${checked ? '1' : '0.35'});
  opacity: ${checked ? '1' : '0'};
  width: 100%;
  height: 100%;
  border-radius: 50%;
  clip-path: circle(40%);
  transition: all .3s ease-in-out;
  background-color: #76d972;
  &:hover {
    cursor: pointer;
  }
`,
)

const ControlCheck = styled.div`
  display: flex;
  align-items: center;
  margin-top: 60px;
`

export default withRouter(ChooseInstitutionComponent)
